import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { MapPin, Clock, Briefcase, CalendarDays, Star, AlertTriangle } from "lucide-react";
import { api } from "@/lib/api";
import { useTitle, formatPrice, formatDuration, formatDate, formatTime, imageUrl, DAYS } from "@/lib/format";
import { DemoBadge, RatingStars, PageLoader, EmptyState } from "@/components/common";
import { NextAvailable } from "@/components/ProviderCard";

function ServiceRow({ s, providerId }) {
  return (
    <div className="flex flex-col gap-4 rounded-2xl border border-slate-200 bg-white p-5 sm:flex-row sm:items-center" data-testid={`service-row-${s.id}`}>
      <div className="flex-1">
        <h3 className="font-heading text-lg font-semibold text-slate-900">{s.name}</h3>
        <p className="mt-1 text-sm text-slate-600">{s.description}</p>
        <div className="mt-2 flex flex-wrap gap-3 text-xs font-medium text-slate-500">
          <span className="flex items-center gap-1"><Clock className="h-3.5 w-3.5" />{formatDuration(s.duration_minutes)}</span>
          <span className="rounded-full bg-slate-100 px-2 py-0.5">{s.service_type}</span>
        </div>
      </div>
      <div className="flex items-center justify-between gap-4 sm:flex-col sm:items-end">
        <p className="font-heading text-2xl font-bold text-slate-900" data-testid={`service-price-${s.id}`}>{formatPrice(s.price)}</p>
        <Link to={`/book/${providerId}?service=${s.id}`} data-testid={`book-service-${s.id}`} className="flex h-11 items-center rounded-xl bg-sky-600 px-5 text-sm font-semibold text-white transition-colors hover:bg-sky-700">Book Now</Link>
      </div>
    </div>
  );
}

function Sidebar({ p }) {
  return (
    <aside className="space-y-5 lg:sticky lg:top-24">
      <div className="rounded-2xl border border-slate-200 bg-white p-6">
        <p className="text-sm text-slate-500">Starting from</p>
        <p className="font-heading text-3xl font-bold text-slate-900">{formatPrice(p.min_price)} <span className="text-sm font-medium text-slate-500">NZD</span></p>
        <div className="mt-3"><NextAvailable next={p.next_available} /></div>
        <Link to={`/book/${p.id}`} data-testid="profile-book-button" className="mt-5 flex h-12 w-full items-center justify-center rounded-xl bg-sky-600 font-semibold text-white hover:bg-sky-700">Book an appointment</Link>
      </div>
      <div className="rounded-2xl border border-slate-200 bg-white p-6" data-testid="business-hours">
        <h3 className="flex items-center gap-2 font-heading font-semibold text-slate-900"><CalendarDays className="h-4 w-4 text-teal-600" />Business hours</h3>
        <ul className="mt-4 space-y-2 text-sm">
          {DAYS.map(([k, label]) => {
            const h = p.working_hours?.[k];
            return (
              <li key={k} className="flex justify-between">
                <span className="text-slate-600">{label}</span>
                <span className={h?.enabled ? "font-medium text-slate-900" : "text-slate-400"}>{h?.enabled ? `${formatTime(h.start)} - ${formatTime(h.end)}` : "Closed"}</span>
              </li>
            );
          })}
        </ul>
      </div>
      <div className="rounded-2xl border border-slate-200 bg-white p-6">
        <h3 className="flex items-center gap-2 font-heading font-semibold text-slate-900"><MapPin className="h-4 w-4 text-teal-600" />Location & service area</h3>
        <p className="mt-3 text-sm text-slate-700">{p.suburb}, {p.city} {p.postcode}</p>
        {p.service_area && <p className="mt-1 text-sm text-slate-500">Serves: {p.service_area}</p>}
      </div>
    </aside>
  );
}

function Reviews({ p }) {
  return (
    <section className="mt-12" data-testid="reviews-section">
      <div className="flex items-center gap-4">
        <h2 className="font-heading text-2xl font-bold text-slate-900">Reviews</h2>
        <span className="flex items-center gap-1.5 rounded-full bg-amber-50 px-3 py-1 text-sm font-semibold text-amber-900" data-testid="profile-rating">
          <Star className="h-4 w-4 fill-amber-400 text-amber-400" />{p.rating_avg ? p.rating_avg.toFixed(1) : "New"} · {p.review_count} reviews
        </span>
      </div>
      {p.reviews.length === 0 ? <p className="mt-4 text-sm text-slate-500">No reviews yet.</p> : (
        <div className="mt-6 grid gap-4 md:grid-cols-2">
          {p.reviews.map((r) => (
            <div key={r.id} className="rounded-2xl border border-slate-200 bg-white p-5" data-testid={`review-${r.id}`}>
              <div className="flex items-center justify-between">
                <p className="font-semibold text-slate-900">{r.customer_name}</p>
                <RatingStars value={r.rating} />
              </div>
              <p className="mt-1 text-xs text-slate-400">{r.service_name} · {formatDate(r.created_at.slice(0, 10), "d MMM yyyy")}</p>
              <p className="mt-3 text-sm text-slate-700">{r.comment}</p>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

function ProfileHeader({ p }) {
  return (
      <div className="relative -mt-14 flex flex-col gap-4 sm:flex-row sm:items-end">
        <img src={imageUrl(p.profile_image)} alt={p.business_name} className="h-28 w-28 rounded-2xl border-4 border-white object-cover shadow-lg" data-testid="provider-avatar" />
        <div className="flex-1 pb-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="rounded-full bg-sky-50 px-2.5 py-1 text-xs font-semibold text-sky-700">{p.category_name}</span>
            {p.is_demo && <DemoBadge />}
            {p.status !== "approved" && <span className="rounded-full bg-amber-50 px-2.5 py-1 text-xs font-semibold capitalize text-amber-700">{p.status} - not public</span>}
          </div>
          <h1 className="mt-2 font-heading text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl" data-testid="provider-name">{p.business_name}</h1>
          <div className="mt-2 flex flex-wrap gap-4 text-sm text-slate-600">
            <span className="flex items-center gap-1"><Star className="h-4 w-4 fill-amber-400 text-amber-400" />{p.rating_avg ? p.rating_avg.toFixed(1) : "New"} ({p.review_count} reviews)</span>
            <span className="flex items-center gap-1"><MapPin className="h-4 w-4" />{p.suburb}, {p.city}</span>
            <span className="flex items-center gap-1"><Briefcase className="h-4 w-4" />{p.years_in_business} years in business</span>
          </div>
        </div>
      </div>
  );
}

export default function ProviderProfile() {
  const { id } = useParams();
  const [p, setP] = useState(null);
  const [error, setError] = useState(false);
  useTitle(p?.business_name);
  useEffect(() => { api.get(`/providers/${id}`).then(({ data }) => setP(data)).catch(() => setError(true)); }, [id]);
  if (error) return <div className="mx-auto max-w-xl px-4 py-20"><EmptyState icon={AlertTriangle} title="Provider not found" text="This business may not be available." testId="provider-not-found" /></div>;
  if (!p) return <PageLoader />;
  return (
    <div data-testid="provider-profile-page">
      <div className="relative h-56 bg-slate-200 sm:h-72 lg:h-80">
        <img src={imageUrl(p.cover_image)} alt="" className="h-full w-full object-cover" data-testid="provider-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-900/50 to-transparent" />
      </div>
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <ProfileHeader p={p} />
        {p.is_demo && (
          <div className="mt-6 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800" data-testid="demo-notice">
            This is a fictional demo business created for testing Bookvera. Bookings are real records in the app but no real service will be provided.
          </div>
        )}
        <div className="mt-10 grid gap-10 pb-20 lg:grid-cols-[1fr_340px]">
          <div>
            <section>
              <h2 className="font-heading text-2xl font-bold text-slate-900">About</h2>
              <p className="mt-3 whitespace-pre-line text-slate-700" data-testid="provider-about">{p.description}</p>
            </section>
            <section className="mt-12" data-testid="services-section">
              <h2 className="font-heading text-2xl font-bold text-slate-900">Services & prices</h2>
              <div className="mt-5 space-y-3">{p.services.map((s) => <ServiceRow key={s.id} s={s} providerId={p.id} />)}</div>
            </section>
            {p.gallery?.length > 0 && (
              <section className="mt-12" data-testid="gallery-section">
                <h2 className="font-heading text-2xl font-bold text-slate-900">Gallery</h2>
                <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-3">
                  {p.gallery.map((g, i) => <img key={g + i} src={imageUrl(g)} alt="" className="aspect-[4/3] w-full rounded-xl object-cover" />)}
                </div>
              </section>
            )}
            <Reviews p={p} />
          </div>
          <Sidebar p={p} />
        </div>
      </div>
    </div>
  );
}
